import { derived, get, writable } from 'svelte/store';
import { isNonProductionAppBuild } from 'src/helpers/dev_tools';
import type { ViewVariant } from 'src/types/DeviceUI';

const STORAGE_KEY = 'shelly_dev_sidepanel_view_switcher';

const readInitialValue = (): boolean => {
	if (!isNonProductionAppBuild()) return false;
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === null) return false;
		return stored === 'true';
	} catch {
		return false;
	}
};

/**
 * Dev-only Main-tab view switcher (Basic / Advanced / future variants).
 * Default off. Production builds are always off. Preference is local-only.
 */
export const sidepanelViewSwitcherEnabled = writable<boolean>(readInitialValue());

sidepanelViewSwitcherEnabled.subscribe((value) => {
	if (!isNonProductionAppBuild()) return;
	try {
		localStorage.setItem(STORAGE_KEY, String(value));
	} catch {
		// localStorage may be unavailable
	}
});

export const sidepanelViewSwitcherAvailable = derived(
	sidepanelViewSwitcherEnabled,
	($enabled) => isNonProductionAppBuild() && $enabled,
);

export const isSidepanelViewSwitcherAvailable = (): boolean => get(sidepanelViewSwitcherAvailable);

export const setSidepanelViewSwitcherEnabled = (enabled: boolean) => {
	sidepanelViewSwitcherEnabled.set(isNonProductionAppBuild() ? enabled : false);
};

/** Session-only per-device Main view selection. Ignored when the switcher flag is off. */
export const sidepanelViewSelection = writable<Record<string, ViewVariant>>({});

export const getSidepanelViewSelection = (deviceId: string | undefined): ViewVariant | undefined => {
	if (!deviceId) return undefined;
	return get(sidepanelViewSelection)[deviceId];
};

export const setSidepanelViewSelection = (deviceId: string, variant: ViewVariant) => {
	sidepanelViewSelection.update((current) => ({ ...current, [deviceId]: variant }));
};
